import { Link } from 'react-router-dom';
import { AlertTriangle, Check } from 'lucide-react';
import type { Conferencia, DetalheDaFolha } from './tipos';

export type Passo = 'folha' | 'impostos' | 'totalizador' | 'conferencia' | 'planilha';

type Estado = 'feito' | 'parcial' | 'atencao' | 'pendente';

const ROTA: Record<Passo, string> = {
  folha: '',
  impostos: '/impostos',
  totalizador: '/totalizador',
  conferencia: '/conferencia',
  planilha: '/planilha',
};

const BARRA: Record<Estado, string> = {
  feito: 'border-t-success',
  parcial: 'border-t-success/50',
  atencao: 'border-t-warning',
  pendente: 'border-t-border',
};

/**
 * O caminho de uma competência, sempre à vista no topo: a folha do escritório,
 * os impostos que chegam depois, o totalizador, a conferência e a planilha do
 * ERP. Os vínculos de setor e de rubrica não são passo do mês: são cadastro,
 * feito uma vez — setor ou rubrica nova só aparece como pendência.
 *
 * É o mesmo molde da cotação, porque o trabalho é igual: sequencial, com
 * arquivos que chegam em momentos diferentes. O passo bloqueado diz o motivo,
 * em vez de só não abrir.
 */
export default function PassosDaFolha({
  folha,
  conferencia,
  atual,
}: {
  folha: DetalheDaFolha;
  conferencia?: Conferencia;
  atual: Passo;
}) {
  const comRelatorio = folha.setores.filter((setor) => setor.relatorio_recebido).length;
  const semCentro = folha.setores.filter((setor) => setor.centro_custo_id === null).length;
  const pendentes = semCentro + folha.rubricas_pendentes.length;

  const passos: { id: Passo; rotulo: string; detalhe: string; estado: Estado; bloqueado?: string }[] = [
    {
      id: 'folha',
      rotulo: 'Folha do escritório',
      detalhe: `${folha.setores.length} setores · ${folha.empresa.funcionarios} funcionários`,
      estado: 'feito',
    },
    {
      id: 'impostos',
      rotulo: 'Impostos por setor',
      detalhe:
        comRelatorio === 0
          ? 'Ainda pelo PDF da folha'
          : `${comRelatorio} de ${folha.setores.length} pelo escritório`,
      estado: comRelatorio === 0 ? 'pendente' : comRelatorio === folha.setores.length ? 'feito' : 'parcial',
    },
    {
      id: 'totalizador',
      rotulo: 'Totalizador',
      detalhe: folha.totalizador_recebido ? 'Recebido' : 'Traz o pró-labore e os totais',
      estado: folha.totalizador_recebido ? 'feito' : 'pendente',
    },
    {
      id: 'conferencia',
      rotulo: 'Conferência',
      detalhe: conferencia
        ? conferencia.aprovada
          ? 'As sete checagens passaram'
          : 'Há diferença a olhar'
        : 'Sete checagens automáticas',
      estado: conferencia ? (conferencia.aprovada ? 'feito' : 'atencao') : 'pendente',
    },
    {
      id: 'planilha',
      rotulo: 'Planilha para o ERP',
      detalhe: folha.lancamentos > 0 ? `${folha.lancamentos} lançamentos` : 'Gera o arquivo do Compumate',
      estado: folha.status === 'exportada' ? 'feito' : 'pendente',
      // Mesma regra do servidor: sem cadastro completo, faltaria linha no lote.
      bloqueado: pendentes > 0 ? `${pendentes} ${pendentes === 1 ? 'item' : 'itens'} a cadastrar antes` : undefined,
    },
  ];

  return (
    <nav aria-label="Etapas da folha" className="mb-lg overflow-hidden rounded-lg border border-border bg-surface">
      <ol className="grid sm:grid-cols-3 lg:grid-cols-5">
        {passos.map((passo, indice) => {
          const aqui = passo.id === atual;

          const conteudo = (
            <>
              <span
                className={[
                  'flex size-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
                  passo.estado === 'feito'
                    ? 'bg-success text-white'
                    : passo.estado === 'atencao'
                      ? 'bg-warning text-white'
                      : aqui
                        ? 'bg-accent text-white'
                        : passo.estado === 'parcial'
                          ? 'border-2 border-success text-success'
                          : 'border border-border text-muted',
                ].join(' ')}
                aria-hidden="true"
              >
                {passo.estado === 'feito' ? <Check size={15} /> : passo.estado === 'atencao' ? <AlertTriangle size={13} /> : indice + 1}
              </span>
              <span className="min-w-0">
                <span className={`block text-[13px] leading-tight font-bold ${aqui ? 'text-accent' : passo.bloqueado ? 'text-muted' : 'text-ink'}`}>
                  {passo.rotulo}
                </span>
                <span className="mt-[2px] block text-[11px] leading-snug text-muted">{passo.bloqueado ?? passo.detalhe}</span>
              </span>
            </>
          );

          const classe = [
            'flex h-full items-start gap-sm border-t-[3px] px-md py-sm transition-colors',
            aqui ? 'border-t-accent bg-accent/5' : BARRA[passo.estado],
            indice > 0 ? 'sm:border-l sm:border-l-border' : '',
          ].join(' ');

          return (
            <li key={passo.id} className="border-b border-border last:border-b-0 sm:border-b-0">
              {passo.bloqueado ? (
                <span className={`${classe} cursor-not-allowed opacity-70`} aria-disabled="true">
                  {conteudo}
                </span>
              ) : (
                <Link to={`/folha/${folha.id}${ROTA[passo.id]}`} aria-current={aqui ? 'step' : undefined} className={`${classe} hover:bg-surface-subtle`}>
                  {conteudo}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
